import { Accordion } from '@ui-kit'
import React, { useMemo } from 'react'
import { HybridLayoutFieldMapOnType } from './hybird-fields/HybridLayoutsMapOnFieldType'
import { HybridLayoutNestedDefaultRowPropsType } from './hybridLayout.types'
import { ElementType } from '../../types/layout.types'
import {
  ApplicationVersionCustomComponent,
  ExceptionIdcCodesCustomComponent,
} from './HybridLayoutCustomInRowComponent'
import {
  ENTITY_MAP,
  fieldKeyEntityMapping,
  HYBRID_LAYOUT_COUSTOM_IN_ROW_COMPONENTS_CONSTANTS,
  MATCH_DEDUPE_RESULT_LABEL,
} from '../../utils/constants'
import { RootState, useAppSelector } from '../../utils/agent.store'
import { DedupeFieldNameType } from '../../types/types'
import {
  getDedupeResult,
  getDedupeResultLabel,
} from '../../utils/helperFunctions'

const gridTemplateObject: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
}

const MatchFoundHeading = (
  label: string,
  fieldKeyName: string
): JSX.Element => {
  const automationStagedDetails = useAppSelector(
    (state: RootState) => state?.workflowData.automationStagedDetails
  )
  const { result: matchResult, records } = (fieldKeyEntityMapping[
    fieldKeyName as DedupeFieldNameType
  ] &&
    Object.entries(automationStagedDetails?.dedup_results || {})?.find(
      ([key]) => {
        return (
          key ===
          fieldKeyEntityMapping[
            fieldKeyName as DedupeFieldNameType
          ].toLowerCase()
        )
      }
    )?.[1]) || { result: '', records: 0 }

  const dedupeResult =
    ENTITY_MAP[fieldKeyName as keyof typeof ENTITY_MAP] && matchResult
      ? getDedupeResult(fieldKeyName, matchResult, records)
      : null

  const dedupeLabel = dedupeResult ? getDedupeResultLabel(dedupeResult) : ''
  const isRecordCreated =
    dedupeLabel === MATCH_DEDUPE_RESULT_LABEL.RECORD_CREATED

  const handleClick = () => {
    const event = new CustomEvent('showPotentialMatchTab', {
      detail: { fieldKeyName },
    })
    window.dispatchEvent(event)
  }
  return (
    <div className="flex items-center gap-2">
      <div>{label}</div>
      {dedupeResult && (
        <button
          type="button"
          tabIndex={0}
          className={`ml-auto text-sm text-border-50  h-full  ${
            isRecordCreated ? '' : 'cursor-pointer underline'
          }`}
          onClick={(e) => {
            if (isRecordCreated) return
            e?.stopPropagation()
            handleClick()
          }}
          onKeyDown={(e) => {
            if (isRecordCreated) return
            if (e.key === 'Enter' || e.key === ' ') {
              e?.stopPropagation()
              handleClick()
            }
          }}
        >
          {dedupeLabel}
        </button>
      )}
    </div>
  )
}

export const HybridLayoutNestedDefaultRow: React.FC<HybridLayoutNestedDefaultRowPropsType> =
  React.memo(
    ({
      row,
      fullLayout,
      isAssociated,
      associatedObject,
      formUniqueId,
      openAllAccordions,
      customInRowComponent,
      rootRowIndex,
      rowIndex: layoutRowIndex,
      mode,
      task,
    }) => {
      const columns = useMemo(() => row?.columns || [], [row?.columns])
      const maxRows = useMemo(
        () =>
          Math.max(
            ...(columns.map((col) => col?.elements?.length || 0) || [0])
          ),
        [columns]
      )

      const fieldKeyName = row?.columns?.[0]?.elements?.[0]?.keyName
      const hasMatchResult =
        fieldKeyName &&
        fieldKeyEntityMapping[fieldKeyName as DedupeFieldNameType]

      return (
        <Accordion
          borderColor="secondary-450"
          accordianHeightPadding="px-5 h-12"
          isConditional={true}
          heading={
            hasMatchResult ? (
              MatchFoundHeading(row?.label ?? '', fieldKeyName)
            ) : (
              <div>{row?.label ?? ''}</div>
            )
          }
          isDefaultActive={openAllAccordions}
        >
          <div
            className={`grid ${
              gridTemplateObject[columns.length || 1]
            } gap-4 p-4`}
          >
            {Array.from({ length: maxRows }).map((_, rowIndex) =>
              columns.map((col) => {
                const element = col?.elements[rowIndex]
                return (
                  <div
                    key={`${col.columnId}-${rowIndex}`}
                    className="col-span-1"
                  >
                    {element ? (
                      <HybridLayoutFieldMapOnType
                        element={element as ElementType}
                        fullLayout={fullLayout}
                        isAssociated={isAssociated}
                        associatedObject={associatedObject}
                        formUniqueId={formUniqueId}
                        mode={mode}
                      />
                    ) : (
                      // Placeholder to preserve grid cell so other columns don't shift left
                      <div className="invisible select-none">&nbsp;</div>
                    )}
                  </div>
                )
              })
            )}
          </div>
          <div
            className={`grid ${
              gridTemplateObject[columns.length || 1]
            } gap-4 px-4 py-2`}
          >
            {customInRowComponent?.map((customInRowComponent) => {
              if (
                customInRowComponent?.key ===
                  HYBRID_LAYOUT_COUSTOM_IN_ROW_COMPONENTS_CONSTANTS?.APPLICATION_VERSION &&
                rootRowIndex === layoutRowIndex
              ) {
                return <ApplicationVersionCustomComponent mode={mode} />
              } else if (
                customInRowComponent?.key ===
                  HYBRID_LAYOUT_COUSTOM_IN_ROW_COMPONENTS_CONSTANTS?.EXCEPTION_ICD_CODES &&
                rootRowIndex === layoutRowIndex
              ) {
                return (
                  <>
                    <ExceptionIdcCodesCustomComponent mode={mode} task={task} />
                  </>
                )
              }
            })}
          </div>
        </Accordion>
      )
    }
  )
HybridLayoutNestedDefaultRow.displayName = 'HybridLayoutNestedDefaultRow'
